import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BrowserUtility } from './../utility/browser.utility';
import DOMPurify from 'dompurify';
import { LocalizationService } from '../service/localization.service';

@Injectable({
  providedIn: 'root'
})

export class BrowserContextService {
  possibleLanguageParams: string[] = ['cultureCode', 'ui_locales'];
  possibleClientCountryParams: string[] = ['clientCountry', 'clientCountryId'];
  urlRegex: RegExp = new RegExp(/^(https?):\/\/[^\s$.?#].[^\s]*$/g);
  urlHasParams: boolean = false;
  urlParams: any = null;
  clientCountryCode: string | null;
  locale: string = 'cultureCode';
  cultureCodeKey: string = 'culture-code';
  clientCountryCodeKey: string = 'clientCountry';
  clientIdKey: string = 'client_id';
  cancelRedirectKey: string = 'cancel_redirect_uri';
  redirectUriKey: string = 'redirect_uri';
  mobileNumberKey: string = 'num';
  id_tokenKey: string = 'token';
  browserUtil: BrowserUtility = new BrowserUtility();
  constructor(private localizationService?: LocalizationService) {
    const browserUrl = this.urlRegex.test(window.location.href) ? window.location.href : null;
    this.urlHasParams = browserUrl?.indexOf('?') > 0;
    this.urlParams = this.urlHasParams ? this.getParams(browserUrl) : null;
  }

  /** 
   * @public GetId_Token
   * @summary  
   * 1. Get from url/query string (save to session storage)
   * 2. If not there, check session storage
   * 3. if not in session, show no logo?
   * 4. Then, at some point, clear id from session storage??
   */
  public GetId_Token(): string | null {
    let ccid: string | null = null;
    // Order of importance for sources
    // 1. If value is available in url, get it there
    if (this.urlHasParams) {
      ccid = this.tryGetValueFromQueryString(['ChangeMfaJwtToken']);
      if (ccid) {
        this.browserUtil.saveToSessionStorage(this.id_tokenKey, ccid);
        return ccid;
      }
    }
    // 2. if not, check storage for the associated key
    const storedCode = this.browserUtil.getFromSessionStorage(this.id_tokenKey)
    if (storedCode) {
      return storedCode;
    }
    return "";
  }

  /** 
     * @public Getlocale
     * @summary  
     * 1. Get from url/query string (save to session storage)
     * 2. If not there, check session storage
     * 3. if not in session, show no logo?
     * 4. Then, at some point, clear id from session storage??
     */
  public GetLocale(): string | null {
    let ccid: string | null = null;
    // Order of importance for sources
    // 1. If value is available in url, get it there
    if (this.urlHasParams) {
      ccid = this.tryGetValueFromQueryString(['cultureCode']);
      if (ccid) {
        this.browserUtil.saveToSessionStorage(this.locale, ccid);
        return ccid;
      }
    }
    // 2. if not, check storage for the associated key
    const storedCode = this.browserUtil.getFromSessionStorage(this.locale)
    if (storedCode) {
      return storedCode;
    }
    return "";
  }

  /** 
   * @public GetMNumber
   * @summary  
   * 1. Get from url/query string (save to session storage)
   * 2. If not there, check session storage
   * 3. if not in session, show no logo?
   * 4. Then, at some point, clear id from session storage??
   */
  public GetMNumber(): string | null {
    let ccid: string | null = null;
    // Order of importance for sources
    // 1. If value is available in url, get it there
    if (this.urlHasParams) {
      ccid = this.tryGetValueFromQueryString(['strongAuthenticationPhoneNumber', 'num']);
      if (ccid) {
        this.browserUtil.saveToSessionStorage(this.mobileNumberKey, ccid);
        return ccid;
      }
    }
    // 2. if not, check storage for the associated key
    const storedCode = this.browserUtil.getFromSessionStorage(this.mobileNumberKey)
    if (storedCode) {
      return storedCode;
    }
    return "";
  }

  /** 
   * @public GetRedirectUri
   * @summary  
   * 1. Get from url/query string (save to session storage)
   * 2. If not there, check session storage
   * 3. if not in session, show no logo?
   * 4. Then, at some point, clear id from session storage??
   */
  public GetRedirectUri(): string | null {
    let ccid: string | null = null;
    // Order of importance for sources
    // 1. If value is available in url, get it there
    if (this.urlHasParams) {
      ccid = this.tryGetValueFromQueryString(['redirect_uri']);
      if (ccid) {
        this.browserUtil.saveToSessionStorage(this.redirectUriKey, ccid);
        return ccid;
      }
    }
    // 2. if not, check storage for the associated key
    const storedCode = this.browserUtil.getFromSessionStorage(this.redirectUriKey)
    if (storedCode) {
      return storedCode;
    }
    return null;
  }

  /** 
  * @public GetCancelRedirectUri
  * @summary  
  * 1. Get from url/query string (save to session storage)
  * 2. If not there, check session storage
  * 3. if not in session, show no logo?
  * 4. Then, at some point, clear id from session storage??
  */
  public GetCancelRedirectUri(): string | null {
    let ccid: string | null = null;
    // Order of importance for sources
    // 1. If value is available in url, get it there
    if (this.urlHasParams) {
      ccid = this.tryGetValueFromQueryString(['cancel_redirect_uri']);
      if (ccid) {
        this.browserUtil.saveToSessionStorage(this.cancelRedirectKey, ccid);
        return ccid;
      }
    }
    // 2. if not, check storage for the associated key
    const storedCode = this.browserUtil.getFromSessionStorage(this.cancelRedirectKey)
    if (storedCode) {
      return storedCode;
    }
    return null;
  }

  /** 
  * @public GetClientId
  * @summary  
  * 1. Get from url/query string (save to session storage)
  * 2. If not there, check session storage
  * 3. if not in session, show no logo?
  * 4. Then, at some point, clear id from session storage??
  */
  public GetClientId(): string | null {
    let ccid: string | null = null;
    // Order of importance for sources
    // 1. If value is available in url, get it there
    if (this.urlHasParams) {
      ccid = this.tryGetValueFromQueryString(['client_id']);
      if (ccid) {
        this.browserUtil.saveToSessionStorage(this.clientIdKey, ccid);
        return ccid;
      }
    }
    // 2. if not, check storage for the associated key
    const storedCode = this.browserUtil.getFromSessionStorage(this.clientIdKey)
    if (storedCode) {
      return storedCode;
    }
    return null;
  }
  /** 
 * @public GetClientCountryId
 * @summary  
 * 1. Get from url/query string (save to session storage)
 * 2. If not there, check session storage
 * 3. if not in session, show no logo?
 * 4. Then, at some point, clear id from session storage??
 */
  public GetClientCountryId(): string | null {
    let ccid: string | null = null;
    // Order of importance for sources
    // 1. If value is available in url, get it there
    if (this.urlHasParams) {
      ccid = this.tryGetValueFromQueryString(['clientCountry', 'clientCountryId']);
      if (ccid) {
        this.browserUtil.saveToSessionStorage(this.clientCountryCodeKey, ccid);
        return ccid;
      }
    }
    // 2. if not, check storage for the associated key
    const storedCode = this.browserUtil.getFromSessionStorage(this.clientCountryCodeKey)
    if (storedCode) {
      return storedCode;
    }
    return null;
  }

  private isFiveCharCultureCode(code: string): boolean {
    const normalizedCode = this.normalizeCultureCode(code);
    const fiveCharCultureCodeRegEx = new RegExp(/^[a-zA-Z]{2}-[a-zA-Z]{2}$/);
    return fiveCharCultureCodeRegEx.test(normalizedCode || "");
  }

   private normalizeCultureCode(code: string | null): string {
    if (!code) return "";
    const normalized = code.trim();
    if (normalized.toLocaleLowerCase() == 'zh-hant') return 'zh-HK';
    if (normalized.toLocaleLowerCase() == 'zh-hans') return 'zh-CN';
    return normalized;
    }
    
  //#region public methods
  public async GetCultureCode(): Promise<string> {
    let language: string | null = null;

    // 1. URL query string (highest priority)
    if (this.urlHasParams) {
      language = this.tryGetValueFromQueryString(this.possibleLanguageParams);
      if (this.isValidCode(language)) {
        console.log('[BrowserContextService::GetCultureCode] source: URL query string', language);
        this.browserUtil.saveToSessionStorage(this.cultureCodeKey, language);
        return language;
      }
    }

    // 2. Browser language or session storage
    if (!language || !this.isValidCode(language)) {
      language = this.getBrowserLangOrDefault();
      console.log('[BrowserContextService::GetCultureCode] source: browser/session', language);
      this.browserUtil.saveToSessionStorage(this.cultureCodeKey, language);
      return language;
    }

    // 3. API lookup by client country
    if (!language || !this.isValidCode(language)) {
      language = await this.tryFetchDefaultCultureCodeByClientCountry();
      console.log('[BrowserContextService::GetCultureCode] source: API lookup', language);
      this.browserUtil.saveToSessionStorage(this.cultureCodeKey, language);
      return language;
    }

    // 4. Last resort: environment default, falling back to en-US
    language = this.isValidCode(environment.defaultLanguage) ? environment.defaultLanguage : 'en-US';
    console.log('[BrowserContextService::GetCultureCode] source: environment default', language);
    this.browserUtil.saveToSessionStorage(this.cultureCodeKey, language);
    return language;
  }
  //#endregion public methods

  //#region Service methods
  private tryGetValueFromQueryString(possibleParams: string[]): string | null {
    if (this.urlParams) {
      for (const param of possibleParams) {
        if (param in this.urlParams) {
          return this.urlParams[param];
        }
      }
    }
    return null;
  }

  private getParams(url: string): any {
    let parser = document.createElement('a');
    parser.href = DOMPurify.sanitize(url);
    let query = parser.search.substring(1);
    let vars = query.split('&');

    let params = {};
    for (const varSet of vars) {
      const pair = varSet.split('=');
      if (pair.length > 1) {
        params[pair[0]] = decodeURIComponent(pair[1]);
      }
    }
    return params;
  }

  private isValidCode(code: string): boolean {
    return this.isFiveCharCultureCode(code);
  }

  private getBrowserLangOrDefault(): string {
    const storedCode = this.browserUtil.getFromSessionStorage(this.cultureCodeKey);
    if (storedCode && this.isValidCode(storedCode)) return storedCode;

    const browserLang = window?.navigator.language;
    if (browserLang) return browserLang;

    return environment.defaultLanguage;
  }

  private async tryFetchDefaultCultureCodeByClientCountry(): Promise<string> {
    if (!this.localizationService) return "";
    const clientCountryIdRaw = this.GetClientCountryId();
    const clientCountryId = Number(clientCountryIdRaw);
    if (!clientCountryIdRaw || Number.isNaN(clientCountryId)) {
      return "";
    }

    try {
      const cultureCode = await firstValueFrom(this.localizationService.getDefaultCultureCodeByClientCountryV2(clientCountryId));
      const normalizedCultureCode = this.normalizeCultureCode(cultureCode);
      if (this.isValidCode(normalizedCultureCode)) {
        this.browserUtil.saveToSessionStorage(this.cultureCodeKey, normalizedCultureCode);
        return normalizedCultureCode;
      }
    } catch (error) {
      console.error('[BrowserContextService::tryFetchDefaultCultureCodeByClientCountry]', error);
    }
    return "";
  }
  //#endregion Service methods
}
